import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { lazy, Suspense, type ReactNode } from "react";
import type { PlanKey } from "@/lib/stripe-plans";
import { HelmetProvider } from "react-helmet-async";
import CookieConsent from "@/components/CookieConsent";
import GlobalAssistant from "@/components/GlobalAssistant";
import { ProductTourHost } from "@/components/onboarding/ProductTour";

// Eagerly load landing page for fast initial render
import LandingPage from "./pages/LandingPage";

// Lazy load all other pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const ContentStudio = lazy(() => import("./pages/ContentStudio"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const IdeasPage = lazy(() => import("./pages/IdeasPage"));
const FeedsPage = lazy(() => import("./pages/FeedsPage"));
const CommunityInbox = lazy(() => import("./pages/CommunityInbox"));
const TeamsPage = lazy(() => import("./pages/TeamsPage"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const PlannerPage = lazy(() => import("./pages/PlannerPage"));
const AlbumPage = lazy(() => import("./pages/AlbumPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const HelpPage = lazy(() => import("./pages/HelpPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const KnowledgePage = lazy(() => import("./pages/KnowledgePage"));
const AiActivityPage = lazy(() => import("./pages/AiActivityPage"));
const SalesPage = lazy(() => import("./pages/SalesPage"));
const SalesLeadPage = lazy(() => import("./pages/SalesLeadPage"));
const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const PublicLeadFormPage = lazy(() => import("./pages/PublicLeadFormPage"));
const PublicQuotePage = lazy(() => import("./pages/PublicQuotePage"));
const PublicBookingPage = lazy(() => import("./pages/PublicBookingPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const PublicSupportPage = lazy(() => import("./pages/PublicSupportPage"));
const OverviewPage = lazy(() => import("./pages/OverviewPage"));
const ApprovalsPage = lazy(() => import("./pages/ApprovalsPage"));
const CeoPage = lazy(() => import("./pages/CeoPage"));
const CompanyProfilePage = lazy(() => import("./pages/CompanyProfilePage"));
const AutonomySettingsPage = lazy(() => import("./pages/AutonomySettingsPage"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const AdminWorkspaces = lazy(() => import("./pages/AdminWorkspaces"));
const AiSchemalaggningPage = lazy(() => import("./pages/AiSchemalaggningPage"));
const BranschGuidePage = lazy(() => import("./pages/BranschGuidePage"));
const GoogleOAuthCallback = lazy(() => import("./pages/GoogleOAuthCallback"));
const GmailScopeDemo = lazy(() => import("./pages/GmailScopeDemo"));

/* ─── Route configuration ─── */
interface RouteConfig {
  path: string;
  element: ReactNode;
  protected?: boolean;
  requireSubscription?: boolean;
  minPlan?: PlanKey;
}

const publicRoutes: RouteConfig[] = [
  { path: "/", element: <LandingPage /> },
  { path: "/funktioner", element: <FeaturesPage /> },
  { path: "/priser", element: <PricingPage /> },
  { path: "/om-oss", element: <AboutPage /> },
  { path: "/login", element: <Login /> },
  { path: "/register", element: <Register /> },
  { path: "/reset-password", element: <ResetPassword /> },
  { path: "/privacy", element: <PrivacyPolicy /> },
  { path: "/terms", element: <TermsOfService /> },
  { path: "/install", element: <InstallApp /> },
  { path: "/unsubscribe", element: <UnsubscribePage /> },
  { path: "/kontakt", element: <ContactPage /> },
  { path: "/hjalp", element: <HelpPage /> },
  { path: "/blogg", element: <BlogPage /> },
  { path: "/blogg/:slug", element: <BlogPostPage /> },
  { path: "/ai-schemalaggning-sociala-medier", element: <AiSchemalaggningPage /> },
  { path: "/guide/:slug", element: <BranschGuidePage /> },
  { path: "/lead/:slug", element: <PublicLeadFormPage /> },
  { path: "/quote/:token", element: <PublicQuotePage /> },
  { path: "/book/:userId", element: <PublicBookingPage /> },
  { path: "/support/:slug", element: <PublicSupportPage /> },
  { path: "/.lovable/oauth/consent", element: <OAuthConsent /> },
  { path: "/oauth/google/callback", element: <GoogleOAuthCallback /> },
  { path: "*", element: <NotFound /> },
];

const protectedRoutes: RouteConfig[] = [
  { path: "/dashboard", element: <Dashboard /> },
  { path: "/onboarding", element: <Onboarding /> },
  { path: "/profile", element: <ProfilePage /> },
  { path: "/admin", element: <AdminPage /> },
  { path: "/admin/workspaces", element: <AdminWorkspaces /> },
  { path: "/subscription", element: <SubscriptionPage /> },
  { path: "/notifications", element: <NotificationSettings /> },
  { path: "/integrationer", element: <IntegrationsPage /> },
  { path: "/demo/gmail", element: <GmailScopeDemo /> },
];

const premiumRoutes: RouteConfig[] = [
  { path: "/studio", element: <ContentStudio /> },
  { path: "/analytics", element: <AnalyticsPage /> },
  { path: "/ideas", element: <IdeasPage /> },
  { path: "/feeds", element: <FeedsPage /> },
  { path: "/inbox", element: <CommunityInbox />, minPlan: "pro" },
  { path: "/teams", element: <TeamsPage />, minPlan: "agency" },
  { path: "/planner", element: <PlannerPage /> },
  { path: "/album", element: <AlbumPage /> },
  { path: "/minne", element: <KnowledgePage />, minPlan: "pro" },
  { path: "/aktivitet", element: <AiActivityPage />, minPlan: "pro" },
  { path: "/sales", element: <SalesPage />, minPlan: "pro" },
  { path: "/sales/:id", element: <SalesLeadPage />, minPlan: "pro" },
  { path: "/marknadsforing", element: <MarketingPage />, minPlan: "pro" },
  { path: "/kundservice", element: <SupportPage />, minPlan: "pro" },
  { path: "/oversikt", element: <OverviewPage />, minPlan: "pro" },
  { path: "/chef", element: <CeoPage />, minPlan: "pro" },
  { path: "/foretagsprofil", element: <CompanyProfilePage /> },
  { path: "/godkann", element: <ApprovalsPage />, minPlan: "pro" },
  { path: "/installningar/autonomi", element: <AutonomySettingsPage />, minPlan: "pro" },
];

const queryClient = new QueryClient();

const LoadingSpinner = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <HelmetProvider>
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <WorkspaceProvider>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes */}
              {publicRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}

              {/* Protected routes (auth only) */}
              {protectedRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={<ProtectedRoute>{element}</ProtectedRoute>} />
              ))}

              {/* Premium routes (auth + subscription) */}
              {premiumRoutes.map(({ path, element, minPlan }) => (
                <Route key={path} path={path} element={<ProtectedRoute requireSubscription minPlan={minPlan}>{element}</ProtectedRoute>} />
              ))}
            </Routes>
          </Suspense>
          <CookieConsent />
          <GlobalAssistant />
          <ProductTourHost />
          </WorkspaceProvider>
        </AuthProvider>
      </BrowserRouter>
      </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </HelmetProvider>
);

export default App;
